/**
 * <cart-items> — logique panier partagée par le drawer et la page /cart.
 *
 * Toutes les modifications passent par la Cart AJAX API (sans rechargement) :
 *   - quantité / retrait  → POST /cart/change.js
 *   - note / message      → POST /cart/update.js { note }
 *   - code promo           → POST /cart/update.js { discount }
 *   - ajout (fiche produit et cross-sell) → POST /cart/add.js
 *
 * Chaque requête demande le HTML re-rendu des sections panier présentes sur la
 * page (paramètre `sections`, Section Rendering API). On ne remplace que le
 * contenu interne de chaque <cart-items data-cart-root> : le drawer reste
 * ouvert, ses écouteurs (délégués) restent en place. Le Liquid est l'unique
 * source de vérité du HTML.
 *
 * Robustesse réseau (Phase B — invariants du CLAUDE.md) : chaque fetch a un
 * timeout explicite ; le bouton déclencheur est neutralisé pendant la requête
 * et réactivé dans un `finally` ; tout échec affiche un message visible et
 * localisé dans une zone aria-live ([data-cart-error]) ; l'état affiché reste
 * cohérent (quantité affichée = quantité réelle).
 *
 * Progressive enhancement : sans JavaScript, la page /cart fonctionne via ses
 * formulaires natifs (updates[], lien retirer, bouton commander).
 */

const NOTE_DEBOUNCE_MS = 500;

/** Délai max d'une requête panier avant abandon (invariant réseau §1). */
const REQUEST_TIMEOUT_MS = 10000;

/** Durée d'affichage automatique du message d'erreur avant masquage. */
const ERROR_AUTOHIDE_MS = 6000;

/** Racine des routes (préfixe de langue/marché éventuel, ex. « / » ou « /en/ »). */
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** URL d'un point d'entrée de la Cart AJAX API, préfixe de marché inclus. */
function cartUrl(endpoint) {
  return `${ROOT}cart/${endpoint}`;
}

let errorTimer = null;

/** Zone de message d'erreur globale (hors [data-cart-root], survit aux re-rendus). */
function cartErrorEl() {
  return document.querySelector('[data-cart-error]');
}

/**
 * Affiche un message d'erreur visible et localisé dans la zone aria-live.
 * `message` optionnel : si fourni (ex. description d'erreur Shopify déjà
 * localisée), il remplace le texte réseau générique ; sinon on garde le texte
 * par défaut rendu côté serveur.
 */
function showCartError(message) {
  const el = cartErrorEl();
  if (!el) return;
  const textEl = el.querySelector('.cart-error__text');
  if (textEl) {
    if (!el.dataset.defaultText) el.dataset.defaultText = textEl.textContent;
    textEl.textContent = message || el.dataset.defaultText;
  }
  el.hidden = false;
  clearTimeout(errorTimer);
  errorTimer = setTimeout(hideCartError, ERROR_AUTOHIDE_MS);
}

/** Masque le message d'erreur. */
function hideCartError() {
  const el = cartErrorEl();
  clearTimeout(errorTimer);
  if (el) el.hidden = true;
}

/** Message localisé porté par une erreur Shopify (sinon null → texte réseau générique). */
function shopifyErrorMessage(error) {
  return error && typeof error.description === 'string' ? error.description : null;
}

/** Liste des ids de section panier présents sur la page (drawer, page /cart…). */
function cartRootIds() {
  return Array.from(document.querySelectorAll('[data-cart-root]'))
    .map((root) => root.dataset.sectionId)
    .filter(Boolean);
}

/** Applique le HTML re-rendu : remplace le contenu interne de chaque racine panier. */
function applySections(sections) {
  if (!sections) return;
  document.querySelectorAll('[data-cart-root]').forEach((root) => {
    const html = sections[root.dataset.sectionId];
    if (typeof html !== 'string') return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-cart-root]');
    if (fresh) root.replaceChildren(...fresh.childNodes);
  });
}

/** Met à jour tous les compteurs (bulle du header, badge du drawer). */
function updateCount() {
  const container = document.querySelector('[data-cart-contents]');
  const count = container ? Number(container.dataset.cartCount || '0') : 0;

  document.querySelectorAll('[data-cart-count-bubble]').forEach((el) => {
    el.textContent = count;
    el.toggleAttribute('hidden', count === 0);
  });
  document.querySelectorAll('[data-cart-count-badge]').forEach((el) => {
    el.textContent = count;
  });
}

/** POST vers la Cart AJAX API en demandant le re-rendu des sections panier. */
async function postCart(url, body, { formData = false } = {}) {
  const ids = cartRootIds();
  let options;

  if (formData) {
    body.append('sections', ids.join(','));
    body.append('sections_url', window.location.pathname);
    options = {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    };
  } else {
    options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: ids, sections_url: window.location.pathname }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    };
  }

  const response = await fetch(url, options);
  const data = await response.json();
  if (!response.ok) throw data;
  return data;
}

/** Rafraîchit le panier après une requête réussie (sections + compteurs + événement). */
function refresh(data) {
  hideCartError();
  applySections(data.sections);
  updateCount();
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
}

class CartItems extends HTMLElement {
  #noteTimer = null;
  #busy = false;

  connectedCallback() {
    // Les +/- et le bouton « mettre à jour » sont des béquilles JS : masqués côté serveur ? non,
    // ils sont utiles sans JS pour la page /cart. Le bouton « mettre à jour » ne sert qu'au repli
    // sans JS ; avec JS la quantité est enregistrée en direct, on le cache.
    this.querySelector('[data-quantity-submit]')?.setAttribute('hidden', '');

    this.addEventListener('click', (event) => this.#onClick(event));
    this.addEventListener('change', (event) => this.#onChange(event));
    this.addEventListener('input', (event) => this.#onInput(event));
    this.addEventListener('submit', (event) => this.#onSubmit(event));
  }

  #onClick(event) {
    const stepper = event.target.closest('[data-quantity-plus], [data-quantity-minus]');
    if (stepper) {
      const input = stepper.parentElement.querySelector('[data-quantity-input]');
      if (!input) return;
      const step = stepper.hasAttribute('data-quantity-plus') ? 1 : -1;
      const next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
      this.#changeLine(input.dataset.lineKey, next, input);
      return;
    }

    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      this.#changeLine(remove.dataset.lineKey, 0);
      return;
    }

    const discountRemove = event.target.closest('[data-discount-remove]');
    if (discountRemove) {
      event.preventDefault();
      const pill = discountRemove.closest('[data-discount-code]');
      if (pill) this.#applyDiscounts(this.#currentDiscounts().filter((c) => c !== pill.dataset.discountCode));
    }
  }

  #onChange(event) {
    const input = event.target.closest('[data-quantity-input]');
    if (!input) return;
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    this.#changeLine(input.dataset.lineKey, qty, input);
  }

  #onInput(event) {
    const note = event.target.closest('[data-cart-note-input]');
    if (!note) return;
    clearTimeout(this.#noteTimer);
    this.#noteTimer = setTimeout(() => {
      postCart(cartUrl('update.js'), { note: note.value }).catch(() => showCartError());
    }, NOTE_DEBOUNCE_MS);
  }

  #onSubmit(event) {
    const discountForm = event.target.closest('[data-discount-form]');
    if (!discountForm) return;
    event.preventDefault();
    const input = discountForm.querySelector('input[name="discount"]');
    const code = (input?.value || '').trim();
    if (!code) return;
    const existing = this.#currentDiscounts();
    if (!existing.includes(code)) existing.push(code);
    this.#applyDiscounts(existing, code, discountForm);
  }

  /**
   * Change la quantité d'une ligne (ou la retire si quantity=0).
   * Sérialisé (#busy) pour éviter les requêtes concurrentes qui se
   * contrediraient. En cas d'échec : message visible + quantité affichée
   * restaurée à la valeur réelle (input.defaultValue = valeur serveur).
   */
  async #changeLine(key, quantity, inputEl) {
    if (!key) return;
    if (this.#busy) return;
    this.#busy = true;
    try {
      const data = await postCart(cartUrl('change.js'), { id: key, quantity });
      refresh(data);
    } catch (error) {
      if (inputEl) inputEl.value = inputEl.defaultValue;
      showCartError(shopifyErrorMessage(error));
    } finally {
      this.#busy = false;
    }
  }

  #currentDiscounts() {
    return Array.from(this.querySelectorAll('[data-discount-code]')).map((el) => el.dataset.discountCode);
  }

  async #applyDiscounts(codes, submittedCode, form) {
    const errorEl = this.querySelector('[data-discount-error]');
    errorEl?.setAttribute('hidden', '');
    try {
      const data = await postCart(cartUrl('update.js'), { discount: codes.join(',') });
      const rejected =
        submittedCode &&
        (data.discount_codes || []).some((d) => d.code === submittedCode && d.applicable === false);
      if (rejected) {
        errorEl?.removeAttribute('hidden');
        // S-08 : le garde portait sur `form` seulement — or c'est `querySelector`
        // qui peut rendre `null` (champ absent d'un formulaire réécrit), et `.value`
        // levait alors une exception qui avalait la suite du `try`.
        const champCode = form?.querySelector('input[name="discount"]');
        if (champCode) champCode.value = '';
        return;
      }
      refresh(data);
    } catch (error) {
      // Échec réseau (≠ code refusé) → message d'erreur réseau visible.
      showCartError(shopifyErrorMessage(error));
    }
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

/**
 * Interception globale des ajouts au panier (fiche produit + cross-sell + composer).
 * Attachée une seule fois. Sans <cart-items> sur la page, on laisse le
 * navigateur soumettre le formulaire normalement.
 */
if (!window.__cartAddBound) {
  window.__cartAddBound = true;

  // Fermeture manuelle du message d'erreur (délégué, la zone est rendue une fois).
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-error-close]')) hideCartError();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/cart/add')) return;
    if (document.querySelectorAll('[data-cart-root]').length === 0) return;

    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    // FormData capturée AVANT de neutraliser le bouton (un champ disabled serait exclu).
    const body = new FormData(form);
    // Bouton neutralisé pendant la requête → anti double-soumission (G-04).
    submit?.setAttribute('aria-busy', 'true');
    if (submit) submit.disabled = true;

    try {
      const data = await postCart(cartUrl('add.js'), body, { formData: true });
      refresh(data);
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      // Pas de form.submit() automatique : éviter une navigation surprise sur
      // réseau lent/coupé. Message visible + bouton réactivé pour réessayer ;
      // le formulaire natif reste disponible en secours.
      showCartError(shopifyErrorMessage(error));
    } finally {
      submit?.removeAttribute('aria-busy');
      if (submit) submit.disabled = false;
    }
  });
}
